/**
 * Input sanitization utilities to prevent XSS and injection attacks
 */

/**
 * Sanitizes text input by escaping HTML entities
 * @param input Raw user input
 * @returns Sanitized string safe for display
 */
export function sanitizeText(input: string): string {
  if (typeof input !== "string") {
    return "";
  }

  // Escape HTML entities to prevent XSS
  return input
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#x27;")
    .replace(/\//g, "&#x2F;");
}

/**
 * Sanitizes input for use as a title or name
 * @param input Raw user input
 * @param maxLength Maximum allowed length
 * @returns Sanitized and trimmed string
 */
export function sanitizeTitle(input: string, maxLength: number = 200): string {
  if (typeof input !== "string") {
    return "";
  }

  // Remove any control characters and trim
  let sanitized = input
    // eslint-disable-next-line no-control-regex -- stripping control characters is the point of this regex
    .replace(/[\x00-\x1F\x7F]/g, "") // Remove control characters
    .trim();

  // Limit length
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength).trim();
  }

  return sanitized;
}

/**
 * Sanitizes tag input
 * @param tag Raw tag input
 * @returns Sanitized tag
 */
export function sanitizeTag(tag: string): string {
  if (typeof tag !== "string") {
    return "";
  }

  // Remove special characters, only allow alphanumeric, spaces, hyphens, and underscores
  return tag
    .replace(/[^a-zA-Z0-9\s\-_]/g, "")
    .trim()
    .substring(0, 50); // Limit tag length
}

/**
 * Validates and sanitizes email input
 * @param email Raw email input
 * @returns Sanitized email or empty string if invalid
 */
export function sanitizeEmail(email: string): string {
  if (typeof email !== "string") {
    return "";
  }

  email = email.trim().toLowerCase();

  // Basic email validation regex
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  if (!emailRegex.test(email)) {
    return "";
  }

  return email;
}

/**
 * Sanitizes note/content input while preserving basic formatting
 * @param content Raw content input
 * @param maxLength Maximum allowed length
 * @returns Sanitized content
 */
export function sanitizeContent(content: string, maxLength: number = 10000): string {
  if (typeof content !== "string") {
    return "";
  }

  // Preserve newlines but sanitize other HTML
  let sanitized = content
    .split("\n")
    .map((line) => sanitizeText(line))
    .join("\n");

  // Limit length
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }

  return sanitized;
}
